import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  CheckCircle2,
  Sparkles,
  CalendarDays,
  Clock,
  GraduationCap,
  ArrowRight,
  Edit3,
  Phone,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STORAGE_KEY = "ugyc_ss_student_id";

export default function SundaySchool() {
  const [studentId, setStudentId] = useState(localStorage.getItem(STORAGE_KEY));
  const [me, setMe] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async (id = studentId) => {
    if (!id) {
      setMe(null);
      return;
    }
    try {
      const r = await api.get(`/sunday-school/me/${id}`);
      setMe(r.data);
    } catch (err) {
      // student id invalid → clear and start over
      localStorage.removeItem(STORAGE_KEY);
      setStudentId(null);
      setMe(null);
    }
  };

  useEffect(() => {
    api.get("/sunday-school/classes").then((r) => setClasses(r.data));
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegistered = (student) => {
    localStorage.setItem(STORAGE_KEY, student.id);
    setStudentId(student.id);
    refresh(student.id);
  };

  const switchAccount = () => {
    if (window.confirm("This will remove the current student from this device. Continue?")) {
      localStorage.removeItem(STORAGE_KEY);
      setStudentId(null);
      setMe(null);
    }
  };

  return (
    <div data-testid="sunday-school-page" className="bg-[#050505] text-white">
      <section className="py-32 md:py-40 px-6 md:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">Sunday School</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">
            One sign-up. <br />
            <span className="text-[#ff5c00]">Weekly</span> attendance.
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            Register your details once, then mark yourself present every Sunday
            with a single tap. Your attendance counts toward the quarterly
            review and exams.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl">
            <div className="bg-[#0f0f14] border border-white/10 p-5 rounded-sm">
              <CalendarDays className="w-5 h-5 text-[#ff5c00] mb-3" />
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">When</p>
              <p className="text-white text-sm">Every Sunday</p>
            </div>
            <div className="bg-[#0f0f14] border border-white/10 p-5 rounded-sm">
              <Clock className="w-5 h-5 text-[#ff5c00] mb-3" />
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Time</p>
              <p className="text-white text-sm">10:30 — 11:15 AM</p>
            </div>
            <div className="bg-[#0f0f14] border border-white/10 p-5 rounded-sm">
              <GraduationCap className="w-5 h-5 text-[#ff5c00] mb-3" />
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Between</p>
              <p className="text-white text-sm">1st & 2nd Service</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <p data-testid="ss-loading" className="text-zinc-500 font-mono-accent text-xs tracking-widest uppercase">Loading…</p>
          ) : me ? (
            <ReturningStudent me={me} classes={classes} onRefresh={refresh} onSwitch={switchAccount} />
          ) : (
            <RegisterForm classes={classes} onRegistered={handleRegistered} />
          )}
        </div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-[#080808] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">The Classes</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-3">
            Same Word. <span className="text-[#ff5c00]">Different rooms.</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mb-10">
            We teach the same content across every class — just pick the room
            and teacher that suits you best.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {classes.map((c, i) => (
              <div
                key={c.id}
                data-testid={`ss-class-${i}`}
                className="bg-[#0f0f14] border border-white/10 p-6 rounded-sm hover:border-[#ff5c00]/50 transition-colors"
              >
                <BookOpen className="w-5 h-5 text-[#ff5c00] mb-3" />
                <h3 className="font-display text-xl uppercase tracking-tight mb-1">{c.name}</h3>
                {c.teacher && <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Teacher · {c.teacher}</p>}
                {c.description && <p className="text-zinc-400 text-sm leading-relaxed">{c.description}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------- Registration ----------
function RegisterForm({ classes, onRegistered }) {
  const [form, setForm] = useState({ name: "", phone: "", class_id: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const r = await api.post("/sunday-school/register", form);
      onRegistered(r.data);
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not register. Try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="ss-register-card"
      className="glass rounded-sm p-7 md:p-10 max-w-3xl"
    >
      <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#ff5c00] mb-2">
        First time? Register once.
      </p>
      <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mb-2">Sign me up.</h2>
      <p className="text-zinc-400 mb-7 max-w-md">
        We'll remember you on this device so next Sunday all you do is tap
        "Mark Me Present".
      </p>

      <form onSubmit={submit} data-testid="ss-register-form" className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Full Name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="ss-register-name"
              className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
            />
          </label>
          <label className="block">
            <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Phone Number</span>
            <input
              type="tel"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="ss-register-phone"
              placeholder="+971 50 000 0000"
              className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
            />
          </label>
        </div>

        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Pick Your Class</span>
          <select
            required
            value={form.class_id}
            onChange={(e) => setForm({ ...form, class_id: e.target.value })}
            data-testid="ss-register-class"
            className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
          >
            <option value="">Select a class…</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.teacher ? ` · ${c.teacher}` : ""}
              </option>
            ))}
          </select>
        </label>

        {error && <p data-testid="ss-register-error" className="text-red-400 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="ss-register-submit"
          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5"
        >
          {status === "loading" ? "Registering…" : "Register Me"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </motion.div>
  );
}

// ---------- Returning Student ----------
function ReturningStudent({ me, classes, onRefresh, onSwitch }) {
  const [marking, setMarking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  const markPresent = async () => {
    setMarking(true);
    setError("");
    try {
      await api.post(`/sunday-school/attendance/${me.id}`);
      await onRefresh(me.id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not mark attendance.");
    } finally {
      setMarking(false);
    }
  };

  const recent = (me.recent_attendance || []).slice(0, 8);

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="ss-returning-card"
        className="lg:col-span-7 glass rounded-sm p-8 md:p-10"
      >
        <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#ff5c00] mb-2">
          Welcome back
        </p>
        <h2 data-testid="ss-welcome-name" className="font-display text-3xl md:text-5xl uppercase tracking-tight">
          {me.name}
        </h2>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-zinc-400">
          <span data-testid="ss-class-label" className="inline-flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#ff5c00]" /> {me.class_name || "—"}
          </span>
          <span className="inline-flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#ff5c00]" /> {me.phone}
          </span>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10">
          <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
            This Sunday · {me.current_sunday}
          </p>
          {me.marked_today ? (
            <div data-testid="ss-marked-today" className="flex items-center gap-3 text-[#22c55e]">
              <CheckCircle2 className="w-7 h-7" />
              <div>
                <p className="font-display text-2xl uppercase tracking-tight">You're checked in.</p>
                <p className="text-zinc-400 text-sm">See you in class. God bless you!</p>
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={markPresent}
                disabled={marking}
                data-testid="ss-mark-attendance"
                className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-8 py-4 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(255,92,0,0.7)]"
              >
                <CheckCircle2 className="w-4 h-4" /> {marking ? "Marking…" : "Mark Me Present"}
              </button>
              {error && <p data-testid="ss-mark-error" className="text-red-400 text-sm mt-3">{error}</p>}
            </>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-3 text-xs">
          <button
            onClick={() => setEditing((e) => !e)}
            data-testid="ss-edit-toggle"
            className="inline-flex items-center gap-2 border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300 font-mono-accent tracking-widest uppercase px-4 py-2 rounded-sm"
          >
            <Edit3 className="w-3 h-3" /> {editing ? "Close" : "Edit My Details"}
          </button>
          <button
            onClick={onSwitch}
            data-testid="ss-switch-account"
            className="inline-flex items-center gap-2 border border-white/10 hover:border-red-400/50 hover:text-red-400 text-zinc-500 font-mono-accent tracking-widest uppercase px-4 py-2 rounded-sm"
          >
            Not you? Sign Out
          </button>
        </div>

        {editing && <EditPanel me={me} classes={classes} onSaved={() => { onRefresh(me.id); setEditing(false); }} />}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        data-testid="ss-recent-attendance"
        className="lg:col-span-5 bg-[#0f0f14] border border-white/10 rounded-sm p-8"
      >
        <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#ff5c00] mb-2">
          Recent Sundays
        </p>
        <h3 className="font-display text-2xl uppercase tracking-tight mb-6">Your attendance.</h3>

        {recent.length === 0 ? (
          <p className="text-zinc-500 text-sm">No attendance yet. Your record starts this Sunday.</p>
        ) : (
          <ul className="space-y-3">
            {recent.map((date) => (
              <li key={date} data-testid="ss-attendance-row" className="flex items-center gap-3 text-sm">
                <Sparkles className="w-4 h-4 text-[#ff5c00]" />
                <span className="text-zinc-200">{date}</span>
                <span className="ml-auto font-mono-accent text-[10px] tracking-widest uppercase text-[#22c55e]">Present</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-7 pt-6 border-t border-white/10 text-sm text-zinc-400">
          <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Total Sundays</p>
          <p className="font-display text-4xl text-[#ff5c00]">{me.recent_attendance?.length || 0}</p>
        </div>
      </motion.div>
    </div>
  );
}

function EditPanel({ me, classes, onSaved }) {
  const [form, setForm] = useState({ name: me.name, phone: me.phone, class_id: me.class_id });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.put(`/sunday-school/me/${me.id}`, form);
      onSaved();
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    }
  };

  return (
    <form onSubmit={save} data-testid="ss-edit-form" className="mt-6 pt-6 border-t border-white/10 space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Name</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="ss-edit-name"
            className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
          />
        </label>
        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Phone</span>
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="ss-edit-phone"
            className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
          />
        </label>
      </div>
      <label className="block">
        <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Class</span>
        <select
          value={form.class_id}
          onChange={(e) => setForm({ ...form, class_id: e.target.value })}
          data-testid="ss-edit-class"
          className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
        >
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.teacher ? ` · ${c.teacher}` : ""}</option>
          ))}
        </select>
      </label>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="ss-edit-save"
        className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-5 py-3 rounded-sm"
      >
        Save Changes
      </button>
    </form>
  );
}
